"use client"

import {
  CircleBackslashIcon,
  GlobeIcon,
  SpeakerLoudIcon,
} from "@radix-ui/react-icons"
import { ALargeSmallIcon, Settings2Icon } from "lucide-react"
import { useTranslations } from "next-intl"
import { useFormContext } from "react-hook-form"

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Separator } from "@/components/ui/separator"
import { CollapsibleSection } from "@/components/generators/_components/collapsible-section"
import SelectPicker from "@/components/generators/_components/SelectPicker"
import {
  type Buchstabe,
  type FormValuesFantasy,
  type GeschlechtOption,
  type KlangOption,
  type NameszusatzOption,
  type SprachOption,
} from "@/components/generators/fantasynamen/FantasyNameGeneratorSchema"

type FantasyNameGeneratorFieldsProps = {
  subject: string
  isFantasyWorldBuilding?: boolean
}

export default function FantasyNameGeneratorFields({
  isFantasyWorldBuilding = false,
}: FantasyNameGeneratorFieldsProps) {
  const form = useFormContext<FormValuesFantasy>()

  // Translations
  const t = useTranslations("Generator.FantasyNameGenerator")

  const klangOptions: KlangOption[] = [
    { value: "imaginative", label: t("imaginative"), icon: "🧚" },
    { value: "scary", label: t("scary"), icon: "🎃" },
    { value: "cute", label: t("cute"), icon: "🍭" },
    { value: "cool", label: t("cool"), icon: "😎" },
    { value: "funny", label: t("funny"), icon: "🤪" },
  ]

  const spracheOptions: SprachOption[] = [
    { value: "german", label: t("languagePicker.german"), icon: "🇩🇪" },
    { value: "english", label: t("languagePicker.english"), icon: "🇬🇧" },
    { value: "french", label: t("languagePicker.french"), icon: "🇫🇷" },
    { value: "spanish", label: t("languagePicker.spanish"), icon: "🇪🇸" },
    { value: "italian", label: t("languagePicker.italian"), icon: "🇮🇹" },
    { value: "russian", label: t("languagePicker.russian"), icon: "🇷🇺" },
    { value: "japanese", label: t("languagePicker.japanese"), icon: "🇯🇵" },
    { value: "chinese", label: t("languagePicker.chinese"), icon: "🇨🇳" },
    { value: "korean", label: t("languagePicker.korean"), icon: "🇰🇷" },
  ]

  const geschlechtOptions: GeschlechtOption[] = [
    { value: "male", label: t("male"), icon: "♂️" },
    { value: "female", label: t("female"), icon: "♀️" },
  ]

  const namenszusatzOptions: NameszusatzOption[] = [
    { value: "name", label: t("name-affix-options.single-name"), icon: "👤" },
    {
      value: "first- and lastname",
      label: t("name-affix-options.first-and-lastname"),
      icon: "👤👤",
    },
    {
      value: "first- middle- and lastname",
      label: t("name-affix-options.first-middle-lastname"),
      icon: "👤👤👤",
    },
  ]

  const alphabet: Buchstabe[] = [
    "random",
    ...("abcdefghijklmnopqrstuvwxyz".split("") as Buchstabe[]),
  ]

  return (
    <div className="grid gap-4">
      {/* Initial Letter Section */}
      <CollapsibleSection
        title={t("letter")}
        icon={
          <ALargeSmallIcon
            size={16}
            strokeWidth={2}
            className="shrink-0 opacity-60"
            aria-hidden="true"
          />
        }
        defaultOpen={false}
      >
        <FormField
          control={form.control}
          name="buchstabe"
          render={({ field }) => (
            <FormItem className="">
              <FormLabel className="sr-only">{t("letter")}</FormLabel>
              <FormDescription>{t("letterDescription")}</FormDescription>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  className="flex max-w-xs flex-wrap gap-1"
                >
                  {alphabet.map((letter) => (
                    <FormItem key={letter} className="">
                      <FormControl>
                        <RadioGroupItem
                          value={letter}
                          id={letter}
                          className="peer sr-only"
                        />
                      </FormControl>
                      <FormLabel
                        htmlFor={letter}
                        className="flex items-center justify-center rounded-md p-3 font-bold uppercase hover:cursor-pointer hover:bg-muted hover:text-muted-foreground
                      peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-muted [&:has([data-state=checked])]:border-primary"
                      >
                        {letter === "random" ? (
                          <CircleBackslashIcon className="size-4 " />
                        ) : (
                          <span className="size-4 text-center">{letter}</span>
                        )}
                      </FormLabel>
                    </FormItem>
                  ))}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </CollapsibleSection>
      {/* More Options Section */}
      <CollapsibleSection
        title={t("more-options")}
        defaultOpen={true}
        icon={
          <Settings2Icon
            size={16}
            strokeWidth={2}
            className="shrink-0 opacity-60"
            aria-hidden="true"
          />
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {t("more-options-desc")}
          </p>
          <Separator className="my-4" />
          <div className="grid grid-cols-[auto_minmax(100px,130px)] grid-rows-3 items-center gap-4">
            {/* Namenszusatz */}
            {!isFantasyWorldBuilding && (
              <>
                <span className="text-sm">{t("name-affix")}</span>
                <FormField
                  control={form.control}
                  name="nameszusatz"
                  render={({ field }) => {
                    const icon =
                      namenszusatzOptions.find(
                        (option) => option.value === field.value
                      )?.icon ?? "not found"
                    return (
                      <SelectPicker
                        field={field}
                        icon={icon}
                        options={namenszusatzOptions}
                      />
                    )
                  }}
                />
              </>
            )}
            {/* Sprache */}
            <span className="text-sm">{t("language")}</span>
            <FormField
              control={form.control}
              name="sprache"
              render={({ field }) => {
                // find the icon for the selected language
                const icon =
                  spracheOptions.find((option) => option.value === field.value)
                    ?.icon ?? "not found"
                return (
                  <SelectPicker
                    field={field}
                    icon={icon}
                    options={spracheOptions}
                    placeholder={
                      <div className="flex items-center ">
                        <GlobeIcon className="mr-2 size-4 " /> {t("language")}
                      </div>
                    }
                  ></SelectPicker>
                )
              }}
            />
            {/* 3. Geschlecht */}
            {!isFantasyWorldBuilding && (
              <>
                <span className="break-words text-sm">{t("gender")}</span>
                <FormField
                  control={form.control}
                  name="geschlecht"
                  render={({ field }) => {
                    const icon =
                      geschlechtOptions.find(
                        (option) => option.value === field.value
                      )?.icon ?? "not found"
                    return (
                      <SelectPicker
                        field={field}
                        icon={icon}
                        options={geschlechtOptions}
                        placeholder={
                          <div className="flex items-center ">
                            <SpeakerLoudIcon className="mr-2 size-4 text-muted-foreground" />
                            Select
                          </div>
                        }
                      />
                    )
                  }}
                />
              </>
            )}
            {/* 4. Klang */}
            <span className="break-words text-sm">{t("vibe")}</span>
            <FormField
              control={form.control}
              name="klang"
              render={({ field }) => {
                const icon =
                  klangOptions.find((option) => option.value === field.value)
                    ?.icon ?? "not found"
                return (
                  <SelectPicker
                    field={field}
                    icon={icon}
                    options={klangOptions}
                    placeholder={
                      <div className="flex items-center ">
                        <SpeakerLoudIcon className="mr-2 size-4 text-muted-foreground" />
                        Select
                      </div>
                    }
                  />
                )
              }}
            />
          </div>
        </div>
      </CollapsibleSection>
    </div>
  )
}
